import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePlatform } from "@/hooks/usePlatform";
import { cn } from "@/lib/utils";

/**
 * AnimatedRoutes — lightweight, framer-free page-transition wrapper.
 *
 * Strategy: re-key on pathname change so the inner subtree replays an
 * `enter` CSS animation. Animation curve is platform-tuned via
 * data-platform on <html> (set by usePlatform). Honours
 * prefers-reduced-motion by skipping the animation entirely.
 *
 * This is intentionally CSS-only to keep bundle size flat — Framer Motion
 * is reserved for surfaces that genuinely need orchestration (sheets,
 * staggered lists). For full-route push/pop the CSS approach is enough
 * and avoids unmount cost.
 */
interface AnimatedRoutesProps {
  children: ReactNode;
}

export const AnimatedRoutes = ({ children }: AnimatedRoutesProps) => {
  const location = useLocation();
  const { prefersReducedMotion, isMobile } = usePlatform();
  const [animKey, setAnimKey] = useState(location.pathname);
  const lastPath = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== lastPath.current) {
      lastPath.current = location.pathname;
      setAnimKey(location.pathname);
    }
  }, [location.pathname]);

  if (prefersReducedMotion) {
    return <>{children}</>;
  }

  return (
    <div
      key={animKey}
      className={cn(
        "will-change-transform",
        // Phone: subtle slide+fade. Desktop: pure fade.
        isMobile ? "animate-route-push-mobile" : "animate-route-fade-desktop",
      )}
    >
      {children}
    </div>
  );
};

export default AnimatedRoutes;
